import { z } from 'zod';

const slugRegex = /^[a-z0-9-]+$/;
const numericRegex = /^\d+$/;

function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const trimmedOpt = (max: number) => z.string().trim().max(max).nullable().optional();

/**
 * Business profile fields (owner spec 2026-06-11) — all optional. Storage
 * stays UTC; `timezone` is the company's IANA display zone.
 */
const companyProfileFields = {
  legalName: trimmedOpt(160),
  ein: z
    .string()
    .trim()
    .regex(/^\d{2}-?\d{7}$/, 'EIN must be 9 digits (XX-XXXXXXX)')
    .nullable()
    .optional(),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Must be a valid email address')
    .nullable()
    .optional(),
  phone: trimmedOpt(30),
  website: trimmedOpt(200),
  niche: trimmedOpt(80),
  addressLine1: trimmedOpt(120),
  addressLine2: trimmedOpt(120),
  city: trimmedOpt(80),
  state: trimmedOpt(40),
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'ZIP must be 5 digits (or ZIP+4)')
    .nullable()
    .optional(),
  timezone: z
    .string()
    .trim()
    .refine(isValidTimezone, 'Must be a valid IANA time zone (e.g. America/Chicago)')
    .nullable()
    .optional(),
};

export const companyCreateSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, 'Name must be at least 3 characters')
      .max(80, 'Name must be 80 characters or fewer'),
    slug: z
      .string()
      .trim()
      .min(3, 'Slug must be at least 3 characters')
      .max(40, 'Slug must be 40 characters or fewer')
      .regex(slugRegex, 'Slug may only contain lowercase letters, numbers, and hyphens'),
    gaPropertyIdOverride: z
      .string()
      .regex(numericRegex, 'GA4 property ID must be numeric')
      .optional(),
    ...companyProfileFields,
  })
  .strict();
export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;

export const companyUpdateSchema = companyCreateSchema
  .partial()
  .extend({
    isArchived: z.boolean().optional(),
  })
  .strict();
export type CompanyUpdateInput = z.infer<typeof companyUpdateSchema>;

export const companyArchiveSchema = z
  .object({
    isArchived: z.boolean(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .strict();
export type CompanyArchiveInput = z.infer<typeof companyArchiveSchema>;

export const companySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  isArchived: z.boolean(),
  gaPropertyIdOverride: z.string().nullable(),
  legalName: z.string().nullable(),
  ein: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  niche: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string(),
  timezone: z.string().nullable(),
  logoPath: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CompanySummary = z.infer<typeof companySummarySchema>;
